import React from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  HeartHandshake,
  Info,
  LifeBuoy,
  Lock,
  MessageCircle,
  Quote,
  ShieldCheck,
  Sparkles,
  UserRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { DISCLAIMER_TEXT } from "@/components/layout/Footer";
import ResponsiveFaq from "@/components/common/ResponsiveFaq";
import FounderSocialLinks from "@/components/common/FounderSocialLinks";
import RelatedLinks from "@/components/common/RelatedLinks";
import { HEYCATCH_EVENTS, trackOutcome } from "@/lib/heycatch";

/**
 * Founder details, provided by Travis. Nothing here is embellished - the
 * "Why I built Sponsor Me" story below is used exactly as he wrote it.
 */
const FOUNDER = {
  name: "Travis",
  role: "Founder, Sponsor Me",
  location: "",
  photo: "/founder.jpg",
  bio: [
    "My name is Travis. Sponsor Me wasn't created because I studied addiction from the outside - addiction was a part of my life for more than 20 years.",
    "I built this platform so people seeking recovery could more easily find volunteer peer sponsors. Sponsors come from communities such as AA, NA, SMART Recovery and CMA. Browsing is free, and sponsors never pay.",
  ],
  isPlaceholder: false,
};

const WHY_PARAGRAPHS = [
  "My name is Travis, and Sponsor Me wasn't created because I studied addiction from the outside. I created it because addiction was a part of my life for more than 20 years.",
  "During those years, I struggled with many different substances, including heroin, methamphetamine, crack, cocaine, alcohol, pills, and other drugs. Addiction took more from me than I ever thought I could lose. One of the most painful consequences was losing my three children from my life.",
  "I know what it feels like when addiction has taken so much that you wonder how you're ever going to rebuild. I also learned how important connection can be in recovery. Sometimes having another person who understands, listens, and has walked a similar road can make a difference in someone's day.",
  "That's why I built Sponsor Me.",
  "I wanted to create a place where people seeking recovery could more easily find and connect with volunteer peer sponsors from communities such as AA, NA, SMART Recovery, CMA, and others.",
  "Sponsor Me isn't meant to replace treatment, therapy, or professional help. It's about making human connection easier.",
  "If this platform helps even one person find someone they can reach out to when they feel alone, then building it was worth it.",
];

const FAQS = [
  {
    id: "how-it-works",
    q: "How does sponsorship work on Sponsor Me?",
    a: "Browse the directory and read profiles - program, years in recovery, availability and how each person prefers to communicate. When someone looks like a fit, send a message introducing yourself. If you both agree to move forward, you carry on in your own way: messaging here, phone calls, meetings, step work. Sponsor Me handles the introduction; the sponsorship relationship is yours.",
  },
  {
    id: "no-account",
    q: "Do I need an account to look around?",
    a: "No. Browsing sponsor profiles is free and open to everyone, with no account and no legal name required. You only create an account when you want to message someone or save profiles.",
  },
  {
    id: "cost",
    q: "What does it cost?",
    a: "Browsing is free forever. People looking for a sponsor get 20 free messages - messages you receive never count against that. After that, unlimited messaging is $1.99 a week, $4.99 a month, or $29.99 a year. You can cancel any time and keep access until the end of the period you paid for.",
  },
  {
    id: "cant-afford",
    q: "What if I can't afford a membership?",
    a: "Request the 7-Day Recovery Access Pass once per account - no card required. Nobody should lose a recovery connection over the price of a membership.",
  },
  {
    id: "free-for-sponsors",
    q: "Is it really free for sponsors?",
    a: "Yes, forever. Sponsor accounts include unlimited messaging at no cost. Nobody should have to pay to be of service.",
  },
  {
    id: "verified",
    q: "Are sponsors verified or screened?",
    a: "No. Sponsors are volunteers who write their own profiles, and we do not verify anyone's recovery history, identity or intentions. Read profiles carefully, go at your own pace, and use Block and Report - both are always free - if anything feels off. Reports go to a human moderation queue.",
  },
  {
    id: "affiliation",
    q: "Is Sponsor Me affiliated with AA, NA or SMART Recovery?",
    a: "No. Sponsor Me is completely independent and is not affiliated with, operated by, approved by or endorsed by any fellowship or recovery organization. Members simply tell you which pathway they personally take part in.",
  },
  {
    id: "not-treatment",
    q: "Is this treatment or crisis support?",
    a: "No. Sponsor Me is peer support only - not medical treatment, therapy, professional counseling or an emergency service. If you are in crisis or immediate danger, use the crisis lines on our Resources page or contact local emergency services.",
  },
];

const PRINCIPLES = [
  {
    icon: HeartHandshake,
    title: "Peer support, not treatment",
    body: "Sponsor Me connects you with volunteer peer sponsors - people with lived experience who choose to help. They are not therapists, doctors, counselors or crisis responders, and this platform is not treatment, therapy or medical care.",
  },
  {
    icon: Lock,
    title: "Privacy by default",
    body: "No legal name required. You choose a display name, share only what you want, and you can delete your account and profile at any time from Settings.",
  },
  {
    icon: ShieldCheck,
    title: "Safety tools that are always free",
    body: "Block and Report sit inside every conversation and never require a membership. Reports go to a human moderation queue, and accounts that break the community rules are suspended or removed.",
  },
  {
    icon: MessageCircle,
    title: "Free to browse, fair to pay",
    body: "Browsing sponsors is free forever and sponsor accounts are free forever. People looking for a sponsor get 20 free messages. After that, plans start at $1.99 a week. If money is the only thing in the way, there is a free access pass.",
  },
];

function FounderPhoto() {
  const [failed, setFailed] = React.useState(false);
  if (FOUNDER.photo && !failed) {
    return (
      <img
        src={FOUNDER.photo}
        alt={`${FOUNDER.name}, founder of Sponsor Me`}
        onError={() => setFailed(true)}
        data-testid="founder-photo"
        className="w-full h-full object-cover"
      />
    );
  }
  return (
    <div
      data-testid="founder-photo-placeholder"
      className="w-full h-full bg-[#E9F3F3] flex flex-col items-center justify-center gap-3 text-center px-6"
    >
      <span className="w-20 h-20 rounded-full bg-white border border-[#CFE4E4] flex items-center justify-center">
        <UserRound className="w-9 h-9 text-[#1F6F78]" />
      </span>
      <p className="text-sm font-semibold text-[#1F6F78]">Founder photo</p>
      <p className="text-xs text-[#4B5A60] leading-5">
        Add a photo at <code className="font-mono">public/founder.jpg</code> and it appears here
        automatically.
      </p>
    </div>
  );
}

export default function About() {
  return (
    <div data-testid="about-page" className="pb-4">
      {/* ---------------------------------------------------------------- hero */}
      <section className="bg-[#F7F4EF] border-b border-[#E3DED4]">
        <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
          <span className="inline-flex items-center gap-2 rounded-full bg-white border border-[#E3DED4] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#1F6F78]">
            <Sparkles className="w-3.5 h-3.5" />
            About Sponsor Me
          </span>
          <h1
            data-testid="about-heading"
            className="mt-5 font-display text-4xl sm:text-5xl font-bold text-[#1F2A2E] leading-tight max-w-3xl"
          >
            Recovery happens between people, not on a waiting list
          </h1>
          <p
            data-testid="about-definition"
            className="mt-5 text-lg text-[#1F2A2E] leading-8 max-w-3xl font-medium"
          >
            Sponsor Me is an independent recovery connection platform created by Travis from
            personal lived experience with addiction and recovery.
          </p>
          <p className="mt-5 text-lg text-[#4B5A60] leading-8 max-w-3xl">
            Sponsor Me is an independent directory that helps people find volunteer peer sponsors
            across AA, NA, CMA, SMART Recovery and other recovery communities - and then makes that
            first message easy to send.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild data-testid="about-browse-cta" className="bg-[#1F6F78] hover:bg-[#195B63] text-white">
              <Link to="/sponsors">
                Browse sponsors
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="about-sponsor-cta"
              className="border-[#1F6F78] text-[#1F6F78] hover:bg-[#E9F3F3]"
            >
              <Link to="/become-a-sponsor">Become a sponsor</Link>
            </Button>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4">
        {/* ------------------------------------------------------------ founder */}
        <section className="mt-12 sm:mt-16" data-testid="about-founder-section">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1F2A2E]">
            Who is behind Sponsor Me
          </h2>

          <div className="mt-6 grid gap-6 md:grid-cols-[minmax(0,300px)_1fr] items-start">
            <div className="rounded-3xl overflow-hidden border border-[#E3DED4] bg-white aspect-[4/5] md:sticky md:top-24">
              <FounderPhoto />
            </div>

            <div className="rounded-3xl border border-[#E3DED4] bg-white p-6 sm:p-8">
              <h3
                data-testid="founder-name"
                className="font-display text-2xl font-bold text-[#1F2A2E]"
              >
                {FOUNDER.name}
              </h3>
              <p className="mt-1 text-sm font-semibold text-[#1F6F78]" data-testid="founder-role">
                {FOUNDER.role}
                {FOUNDER.location ? ` · ${FOUNDER.location}` : ""}
              </p>

              <div className="mt-4 space-y-3" data-testid="founder-bio">
                {FOUNDER.bio.map((p, i) => (
                  <p key={`founder-bio-${i}`} className="text-[#4B5A60] leading-7">
                    {p}
                  </p>
                ))}
              </div>

              {/* Travis's own personal profiles, provided by him. Labelled as
                  his, not as Sponsor Me business accounts. */}
              <div className="mt-5" data-testid="about-founder-social">
                <p className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
                  Follow Travis, Founder of Sponsor Me
                </p>
                <FounderSocialLinks testIdPrefix="about-founder-social" className="mt-2" />
              </div>

              {FOUNDER.isPlaceholder && (
                <div
                  data-testid="founder-placeholder-notice"
                  className="mt-6 flex gap-3 rounded-2xl border border-[#E6D3A3] bg-[#FDF6E7] p-4"
                >
                  <AlertTriangle className="w-5 h-5 text-[#8A5A2E] shrink-0 mt-0.5" />
                  <p className="text-sm text-[#6B4A22] leading-6">
                    <strong>Placeholder:</strong> the founder name, bio and photo above are
                    placeholders. Nothing about a personal recovery history, credentials or
                    background has been written for you - send those details and they will be
                    dropped in here.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* -------------------------------------------------------------- story */}
        <section className="mt-14" data-testid="about-why-section">
          <div className="rounded-3xl border border-[#E3DED4] bg-[#F7F4EF] p-6 sm:p-10">
            <Quote className="w-8 h-8 text-[#1F6F78]" aria-hidden="true" />
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold text-[#1F2A2E]">
              Why I built Sponsor Me
            </h2>
            <div className="mt-5 space-y-4 max-w-3xl" data-testid="about-why-story">
              {WHY_PARAGRAPHS.map((p, i) => (
                <p key={`about-why-${i}`} className="text-[17px] text-[#3A464B] leading-8">
                  {p}
                </p>
              ))}
            </div>
            <p className="mt-6 font-display text-lg font-bold text-[#1F2A2E]" data-testid="about-why-signature">
              &mdash; Travis
              <span className="block text-sm font-sans font-semibold text-[#1F6F78]">
                Founder, Sponsor Me
              </span>
            </p>
            {/* Building in public: a real recency signal, pointing at the dated
                changelog of work that has actually shipped. No social links are
                listed anywhere because no real ones have been provided. */}
            <div className="mt-6 rounded-2xl bg-[#F7F4EF] border border-[#E3DED4] p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <p className="text-sm text-[#4B5A60] leading-6 flex-1">
                <span className="font-semibold text-[#1F2A2E]">Building Sponsor Me in public.</span>{" "}
                Every change that ships is listed, with dates, on the updates page.
              </p>
              <Button
                asChild
                variant="outline"
                className="bg-white border-[#1F6F78] text-[#1F6F78] hover:bg-[#D7EEF0] shrink-0"
              >
                <Link
                  to="/updates"
                  data-testid="about-updates-link"
                  onClick={() => trackOutcome(HEYCATCH_EVENTS.PRODUCT_UPDATES_CLICKED)}
                >
                  See product updates <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------------------- faq */}
        <section className="mt-14" data-testid="about-faq-section">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1F2A2E]">
            Common questions
          </h2>
          <p className="mt-2 text-[#4B5A60]">
            How sponsorship works here, what it costs, and what this platform is not.
          </p>
          {/* Phones show every answer already open (HeyCatch recorded
              rage-clicks here, with "What does it cost?" among the worst);
              desktop keeps the accordion. Same FAQS array drives both. */}
          <ResponsiveFaq
            items={FAQS}
            testIdPrefix="about-faq"
            priorityIds={["cost", "verified", "how-it-works", "cant-afford"]}
            className="mt-6"
          />
          <p className="mt-4 text-sm text-[#4B5A60]">
            Still have a question?{" "}
            <Link
              to="/contact"
              data-testid="about-faq-contact-link"
              className="font-semibold text-[#1F6F78] hover:underline"
            >
              Send us a message
            </Link>{" "}
            - no account needed.
          </p>
        </section>

        {/* --------------------------------------------------------- principles */}
        <section className="mt-14" data-testid="about-principles-section">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1F2A2E]">
            What Sponsor Me is - and what it is not
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {PRINCIPLES.map((p) => (
              <article
                key={p.title}
                data-testid={`about-principle-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-2xl border border-[#E3DED4] bg-white p-5"
              >
                <span className="w-10 h-10 rounded-xl bg-[#E9F3F3] flex items-center justify-center">
                  <p.icon className="w-5 h-5 text-[#1F6F78]" />
                </span>
                <h3 className="mt-3 font-display text-lg font-bold text-[#1F2A2E]">{p.title}</h3>
                <p className="mt-2 text-sm text-[#4B5A60] leading-6">{p.body}</p>
              </article>
            ))}
          </div>
        </section>

        {/* ----------------------------------------------------- safety notice */}
        <section className="mt-14" data-testid="about-safety-section">
          <div className="rounded-3xl border border-[#E3DED4] bg-white p-6 sm:p-8">
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-xl bg-[#1F2A2E] text-[#F7F4EF] flex items-center justify-center shrink-0">
                <LifeBuoy className="w-5 h-5" />
              </span>
              <div>
                <h2 className="font-display text-xl font-bold text-[#1F2A2E]">
                  If you are in crisis, please reach a human now
                </h2>
                <p className="mt-2 text-[#4B5A60] leading-7">
                  Sponsor Me is not an emergency service and cannot respond to crises. If you are in
                  immediate danger, or thinking about harming yourself, contact your local emergency
                  number or a crisis line right away.
                </p>
                <Button
                  asChild
                  variant="outline"
                  data-testid="about-crisis-link"
                  className="mt-4 border-[#1F2A2E] text-[#1F2A2E] hover:bg-[#F2EFE8]"
                >
                  <Link to="/resources">See crisis lines and resources</Link>
                </Button>
              </div>
            </div>

            <div className="mt-6 flex gap-3 rounded-2xl bg-[#F7F4EF] border border-[#E3DED4] p-4">
              <Info className="w-5 h-5 text-[#4B5A60] shrink-0 mt-0.5" />
              <div className="text-xs text-[#4B5A60] leading-5" data-testid="about-disclaimer">
                <p>{DISCLAIMER_TEXT}</p>
                <p className="mt-2">
                  Sponsor Me does not provide medical treatment, therapy, professional counseling or
                  emergency services, and it is not a replacement for professional addiction
                  treatment. We make no promises about recovery outcomes. Read our{" "}
                  <Link to="/terms" className="font-semibold text-[#1F6F78] hover:underline">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="font-semibold text-[#1F6F78] hover:underline">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ----------------------------------------------------------- closing */}
        <section className="mt-14 mb-4">
          <div className="rounded-3xl bg-[#1F6F78] px-6 py-10 sm:px-10 text-center">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">
              Ready to find someone who gets it?
            </h2>
            <p className="mt-3 text-[#D7EEF0] leading-7 max-w-2xl mx-auto">
              Browsing is free and you do not need an account to look. When you are ready, your
              first 20 messages are free too.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Button
                asChild
                data-testid="about-footer-browse-cta"
                className="bg-white text-[#1F6F78] hover:bg-[#EAF6F6]"
              >
                <Link to="/sponsors">Browse sponsors</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="about-footer-sponsor-cta"
                className="border-white text-white hover:bg-[#195B63]"
              >
                <Link to="/become-a-sponsor">Volunteer as a sponsor</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
      <RelatedLinks
        heading="Keep reading"
        links={[
          { to: "/sponsors", label: "Browse sponsors" },
          { to: "/updates", label: "Product updates" },
          { to: "/contact", label: "Contact Sponsor Me" },
          { to: "/become-a-sponsor", label: "Become a sponsor" },
        ]}
      />
    </div>
  );
}
